/** @jsx jsx */

/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import { useNavigate } from 'js/lib/useRouter';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon, ClockOutlineIcon } from '@coursera/cds-icons';

import CourseInfo from 'bundles/s12n-common/components/s12n-versioning-v2/CourseInfo';
import type { S12nUpdateInfo } from 'bundles/s12n-common/components/s12n-versioning-v2/useFetchS12nVersioningInfo';

import _t from 'i18n!nls/program-home';

const styles = {
  timeAddedText: css`
    display: flex;
    gap: var(--cds-spacing-100);
    margin: var(--cds-spacing-150) 0;
    font-weight: 700;
  `,
  itemHeader: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-100);
  `,
  actionButton: css`
    margin-right: var(--cds-spacing-200);
  `,
};

type Props = {
  update: (fail: boolean) => void;
  confirmDefer: () => void;
  s12nUpdateInfo: S12nUpdateInfo;
};

const UpdateInfo = ({ update, confirmDefer, s12nUpdateInfo }: Props) => {
  const navigateTo = useNavigate();
  return (
    <>
      <Dialog.HeadingGroup>{_t('About this update')}</Dialog.HeadingGroup>

      <Dialog.Content id="dialog-content">
        <Typography2 variant="bodyPrimary" component="p">
          {s12nUpdateInfo.description}
        </Typography2>

        <Typography2 variant="bodySecondary" component="p" css={styles.timeAddedText}>
          <ClockOutlineIcon size="medium" />
          {_t('Total added learning time: #{hoursAdded} hours', { hoursAdded: s12nUpdateInfo.hoursAdded })}
        </Typography2>

        <InlineNotification>
          {_t(
            'Any progress you’ve made, including graded content, will carry over, as long as it’s included in the new version. Note that once you update, you can’t switch back to a previous version.'
          )}
        </InlineNotification>

        {s12nUpdateInfo.coursesAdded.length > 0 && (
          <>
            <Typography2 css={styles.itemHeader} variant="subtitleLarge" component="h2">
              {_t('Courses added')}
            </Typography2>
            {s12nUpdateInfo.coursesAdded.map((course) => (
              <CourseInfo key={course.id} course={course} />
            ))}
          </>
        )}

        {s12nUpdateInfo.coursesRemoved.length > 0 && (
          <>
            <Typography2 css={styles.itemHeader} variant="subtitleLarge" component="h2">
              {_t('Courses removed')}
            </Typography2>
            {s12nUpdateInfo.coursesRemoved.map((course) => (
              <CourseInfo key={course.id} course={course} />
            ))}
          </>
        )}

        <Button
          css={styles.itemHeader}
          variant="ghost"
          iconPosition="after"
          icon={<ArrowNextIcon />}
          onClick={() => {
            navigateTo(`${s12nUpdateInfo.url}#courses`);
          }}
        >
          {_t('Learn more about the changes')}
        </Button>
      </Dialog.Content>

      <Dialog.Actions>
        <Button css={styles.actionButton} size="medium" variant="primary" onClick={() => update(false)}>
          {_t('Update to new version')}
        </Button>
        <Button css={styles.actionButton} size="medium" variant="secondary" onClick={confirmDefer}>
          {_t('Continue with current version')}
        </Button>
      </Dialog.Actions>
    </>
  );
};

export default UpdateInfo;
